import React, { useState, useMemo, useEffect } from 'react'
import { axiosInstance } from '../../../helpers'
import { Container } from '../../../components/Container'
import {
    Theme,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    Button,
    IconButton,
    Toolbar,
    Tooltip,
    FormControlLabel,
    Switch,
    Grid,
    TableSortLabel,
} from '@material-ui/core'
import {
    Visibility,
    VisibilityOff,
    Search,
    KeyboardArrowRight,
    KeyboardArrowLeft,
} from '@material-ui/icons'
import { MissingErrorsItem } from '../../../models/DocumentAudit/types'
import { getEntityClassificationUiName } from '../../../models/DocumentAudit/services'
import { toast } from 'react-toastify'
import { useLeaseAudit } from '../../../hooks/documentAudit/useLeaseAudit'

interface Props {
    theme: Theme
    isActive: boolean
    leaseAuditId: number
}

// Define sort type for table
type Order = 'asc' | 'desc'
type OrderByKey =
    | 'entity_class'
    | 'missing_document_count'
    | 'mismatched_count'
    | 'matched_count'
    | 'total_count'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    table: {
        minWidth: 600,
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    toolbar: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
    },
    title: {
        flex: '1 1 100%',
    },
    filterContainer: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    searchField: {
        marginRight: theme.spacing(2),
        width: '300px',
    },
    highlight: {
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.secondary.light,
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
    tableHead: {
        backgroundColor: theme.palette.primary.main,
        position: 'sticky',
        top: 0,
        zIndex: 2,
    },
    tableHeadCell: {
        color: theme.palette.primary.contrastText,
        fontWeight: 'bold',
        backgroundColor: theme.palette.primary.main,
        position: 'sticky',
        top: 0,
        zIndex: 10,
    },
    splitView: {
        display: 'flex',
        gap: theme.spacing(2),
        height: 'calc(100vh - 160px)',
        maxHeight: 600,
    },
    tableContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
    },
    scrollableTableContainer: {
        overflow: 'auto',
        height: 'calc(100% - 120px)',
        maxHeight: '100%',
    },
    transferButton: {
        minWidth: '40px',
        margin: theme.spacing(1, 0),
    },
    transferButtonContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(1),
    },
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    extractButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

export const MissingErrors = ({ theme, leaseAuditId }: Props) => {
    const classes = useStyles(theme)
    const [selected, setSelected] = useState<number[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [showHidden, setShowHidden] = useState(false)
    const [hiddenEntities, setHiddenEntities] = useState<string[]>([])
    const [order, setOrder] = useState<Order>('desc')
    const [orderBy, setOrderBy] = useState<OrderByKey>('total_count')
    const [selectedModelProvider, setSelectedModelProvider] = useState<string>(
        'gemini',
    )

    const leaseAuditController = useLeaseAudit()
    const missingErrors = leaseAuditController.missingErrors

    useEffect(() => {
        leaseAuditController.fetchMissingErrors(leaseAuditId)
    }, [])

    useEffect(() => {
        if (
            !leaseAuditController.loading.missingErrors &&
            missingErrors.length > 0
        ) {
            // Your functions to run after loading is complete
            console.log('Missing errors loaded:', missingErrors)
            // Add your other functions here
        }
    }, [leaseAuditController.loading.missingErrors, missingErrors])

    // Filter available (unselected) data
    const availableData = useMemo(() => {
        console.log('availableData recalculating with:', {
            missingErrorsLength: missingErrors.length,
            searchTerm,
            hiddenEntities,
            showHidden,
            selected,
            order,
            orderBy,
        })
        return missingErrors
            .filter(
                (item) =>
                    !selected.includes(item.entity_class_id) &&
                    (showHidden ||
                        !hiddenEntities.includes(item.entity_class)) &&
                    item.entity_class
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()),
            )
            .sort((a, b) => {
                const isAsc = order === 'asc'

                if (orderBy === 'entity_class') {
                    return isAsc
                        ? a.entity_class.localeCompare(b.entity_class)
                        : b.entity_class.localeCompare(a.entity_class)
                }

                return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy]
            })
    }, [
        missingErrors,
        searchTerm,
        hiddenEntities,
        showHidden,
        selected,
        order,
        orderBy,
    ])

    // Filter selected data
    const selectedData = useMemo(() => {
        return missingErrors
            .filter((item) => selected.includes(item.entity_class_id))
            .sort((a, b) => {
                const isAsc = order === 'asc'

                if (orderBy === 'entity_class') {
                    return isAsc
                        ? a.entity_class.localeCompare(b.entity_class)
                        : b.entity_class.localeCompare(a.entity_class)
                }

                return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy]
            })
    }, [missingErrors, selected, order, orderBy])

    const handleSelect = (entityClassId: number) => {
        if (!selected.includes(entityClassId)) {
            setSelected([...selected, entityClassId])
        }
    }

    const handleUnselect = (entityClassId: number) => {
        setSelected(selected.filter((id) => id !== entityClassId))
    }

    const handleSelectAll = () => {
        const allAvailableIds = availableData.map(
            (item) => item.entity_class_id,
        )
        setSelected([...selected, ...allAvailableIds])
    }

    const handleUnselectAll = () => {
        setSelected([])
    }

    const handleToggleVisibility = (entityClass: string) => {
        if (hiddenEntities.includes(entityClass)) {
            setHiddenEntities(hiddenEntities.filter((e) => e !== entityClass))
        } else {
            setHiddenEntities([...hiddenEntities, entityClass])
        }
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value)
    }

    const handleRequestSort = (property: OrderByKey) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    // Renders a table with entity data
    const renderTable = (
        data: MissingErrorsItem[],
        isSelectedTable: boolean = false,
    ) => (
        <div className={classes.scrollableTableContainer}>
            <TableContainer style={{ maxHeight: '100%' }}>
                <Table
                    className={classes.table}
                    size="medium"
                    stickyHeader
                    aria-label={
                        isSelectedTable
                            ? 'selected missing errors table'
                            : 'available missing errors table'
                    }
                >
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.tableHeadCell}>
                                <TableSortLabel
                                    active={orderBy === 'entity_class'}
                                    direction={
                                        orderBy === 'entity_class'
                                            ? order
                                            : 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort('entity_class')
                                    }
                                >
                                    Entity Class
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                className={classes.tableHeadCell}
                            >
                                <TableSortLabel
                                    active={
                                        orderBy === 'missing_document_count'
                                    }
                                    direction={
                                        orderBy === 'missing_document_count'
                                            ? order
                                            : 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort(
                                            'missing_document_count',
                                        )
                                    }
                                >
                                    Missing Doc
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                className={classes.tableHeadCell}
                            >
                                <TableSortLabel
                                    active={orderBy === 'mismatched_count'}
                                    direction={
                                        orderBy === 'mismatched_count'
                                            ? order
                                            : 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort('mismatched_count')
                                    }
                                >
                                    Mis-Matched
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                className={classes.tableHeadCell}
                            >
                                <TableSortLabel
                                    active={orderBy === 'matched_count'}
                                    direction={
                                        orderBy === 'matched_count'
                                            ? order
                                            : 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort('matched_count')
                                    }
                                >
                                    Matched
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                className={classes.tableHeadCell}
                            >
                                <TableSortLabel
                                    active={orderBy === 'total_count'}
                                    direction={
                                        orderBy === 'total_count'
                                            ? order
                                            : 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort('total_count')
                                    }
                                >
                                    Total
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="center"
                                className={classes.tableHeadCell}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length > 0 ? (
                            data.map((row) => {
                                const isHidden = hiddenEntities.includes(
                                    row.entity_class,
                                )
                                const missingPercentage =
                                    (row.missing_document_count /
                                        row.total_count) *
                                    100

                                return (
                                    <TableRow
                                        hover
                                        key={row.entity_class_id}
                                        style={
                                            isHidden &&
                                            !showHidden &&
                                            !isSelectedTable
                                                ? { display: 'none' }
                                                : {}
                                        }
                                    >
                                        <TableCell component="th" scope="row">
                                            {getEntityClassificationUiName(
                                                row.entity_class,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {(
                                                (row.missing_document_count /
                                                    row.total_count) *
                                                100
                                            ).toFixed(1)}
                                            % ({row.missing_document_count})
                                        </TableCell>
                                        <TableCell align="right">
                                            {(
                                                (row.mismatched_count /
                                                    row.total_count) *
                                                100
                                            ).toFixed(1)}
                                            % ({row.mismatched_count})
                                        </TableCell>
                                        <TableCell align="right">
                                            {(
                                                (row.matched_count /
                                                    row.total_count) *
                                                100
                                            ).toFixed(1)}
                                            % ({row.matched_count})
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.total_count}
                                        </TableCell>
                                        <TableCell align="center">
                                            {isSelectedTable ? (
                                                <IconButton
                                                    onClick={() =>
                                                        handleUnselect(
                                                            row.entity_class_id,
                                                        )
                                                    }
                                                    color="primary"
                                                    aria-label="move to available"
                                                >
                                                    <KeyboardArrowLeft />
                                                </IconButton>
                                            ) : (
                                                <>
                                                    <IconButton
                                                        onClick={() =>
                                                            handleSelect(
                                                                row.entity_class_id,
                                                            )
                                                        }
                                                        color="primary"
                                                        aria-label="move to selected"
                                                    >
                                                        <KeyboardArrowRight />
                                                    </IconButton>
                                                    <Tooltip
                                                        title={
                                                            isHidden
                                                                ? 'Show entity'
                                                                : 'Hide entity'
                                                        }
                                                    >
                                                        <IconButton
                                                            onClick={() =>
                                                                handleToggleVisibility(
                                                                    row.entity_class,
                                                                )
                                                            }
                                                        >
                                                            {isHidden ? (
                                                                <VisibilityOff />
                                                            ) : (
                                                                <Visibility />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className={classes.emptyState}
                                >
                                    {isSelectedTable
                                        ? 'No entities selected yet. Use the arrow buttons to select entities from the left table.'
                                        : 'No entities available. All entities have been selected or filtered out.'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )

    if (leaseAuditController.loading.missingErrors) {
        return <div>Loading...</div>
    }

    return (
        <div className={classes.root}>
            <Paper className={classes.filterContainer}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item>
                        <TextField
                            className={classes.searchField}
                            variant="outlined"
                            placeholder="Search entity class..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <Search color="action" />,
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showHidden}
                                    onChange={() => setShowHidden(!showHidden)}
                                    color="primary"
                                />
                            }
                            label="Show hidden entities"
                        />
                    </Grid>
                    <Grid item>
                        {/* model selector */}
                        <Container style={{ marginBottom: theme.spacing(2) }}>
                            <FormControl>
                                <FormLabel>Model Provider</FormLabel>
                                <Select
                                    value={selectedModelProvider}
                                    onChange={(e) => {
                                        setSelectedModelProvider(
                                            e.target.value as string,
                                        )
                                    }}
                                >
                                    <MenuItem value="gemini">Gemini</MenuItem>
                                    <MenuItem value="openai">Open AI</MenuItem>
                                </Select>
                            </FormControl>
                        </Container>
                    </Grid>
                </Grid>
            </Paper>

            <div className={classes.splitView}>
                {/* Left Table - Available Entities */}
                <Paper className={classes.tableContainer}>
                    <Toolbar className={classes.toolbar}>
                        <Typography
                            className={classes.title}
                            variant="h6"
                            component="div"
                        >
                            Available Entities
                        </Typography>
                        <Tooltip title="Select all entities">
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleSelectAll}
                                disabled={availableData.length === 0}
                            >
                                Select All
                            </Button>
                        </Tooltip>
                    </Toolbar>
                    {renderTable(availableData)}
                </Paper>

                {/* Right Table - Selected Entities */}
                <Paper className={classes.tableContainer}>
                    <Toolbar className={classes.toolbar}>
                        <Typography
                            className={classes.title}
                            variant="h6"
                            component="div"
                        >
                            Selected Entities ({selectedData.length})
                        </Typography>
                        <Tooltip title="Remove all selections">
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleUnselectAll}
                                disabled={selectedData.length === 0}
                            >
                                Clear All
                            </Button>
                        </Tooltip>
                    </Toolbar>
                    {renderTable(selectedData, true)}

                    {selectedData.length > 0 && (
                        <div className={classes.extractButtonContainer}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    if (selected.length > 0) {
                                        axiosInstance
                                            .post(
                                                'document-audit/deliverable/fix_missing_errors/',
                                                {
                                                    entity_class_ids: selected,
                                                    model_provider: selectedModelProvider,
                                                },
                                            )
                                            .then((res) => {
                                                toast.success(
                                                    'Fixing missing errors.',
                                                )
                                            })
                                            .catch((e) => {
                                                toast.error(
                                                    'Error fetching missing errors',
                                                )
                                            })
                                    }
                                }}
                            >
                                Extract Selected Fields ({selectedData.length})
                            </Button>
                        </div>
                    )}
                </Paper>
            </div>
        </div>
    )
}
